import React from "react";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { LinkedMember, ConnectionRoleType, ListingWithDetails } from "../types";

interface LinkedMemberSelectProps {
  value: LinkedMember[];
  onChange: (members: LinkedMember[], emailInvites?: string[]) => void;
  members: ListingWithDetails[];
  role: ConnectionRoleType;
  creatable?: boolean;
  excludeName?: string;
  placeholder?: string;
  maxSelections?: number;
  lockedIds?: string[];
}

const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const LinkedMemberSelect: React.FC<LinkedMemberSelectProps> = ({
  value,
  onChange,
  members,
  role,
  creatable = false,
  excludeName,
  placeholder = "Select members...",
  maxSelections,
  lockedIds = [],
}) => {
  // Convert members to select options
  const memberOptions = members
    .filter((member) => !excludeName || member.name !== excludeName)
    .map((member) => ({
      value: member.id,
      label: member.name,
    }));

  // Convert LinkedMember[] to select value format
  const selectValue = value.map((member) => ({
    value: member.isRegistered ? member.id : member.email || member.name,
    label: member.name,
  }));

  const handleChange = (selectedOptions: any) => {
    const lockedMembers = value.filter((m) => lockedIds.includes(m.id));

    if (!selectedOptions) {
      onChange(lockedMembers, []);
      return;
    }

    const linkedMembers: LinkedMember[] = [...lockedMembers];
    const emailInvites: string[] = [];

    selectedOptions.forEach((option: any) => {
      if (lockedIds.includes(option.value)) {
        return;
      }
      if (creatable && isValidEmail(option.value)) {
        emailInvites.push(option.value);
        linkedMembers.push({
          id: `invite_${option.value}`,
          name: option.value,
          isRegistered: false,
          email: option.value,
          role,
        });
      } else {
        const member = members.find((m) => m.id === option.value);
        if (member) {
          linkedMembers.push({
            id: member.id,
            name: member.name,
            isRegistered: true,
            imageUrl: member.imageUrl,
            role,
          });
        }
      }
    });

    onChange(linkedMembers, emailInvites);
  };

  const SelectComponent = creatable ? CreatableSelect : Select;

  return (
    <SelectComponent
      isMulti
      value={selectValue}
      onChange={(newValue) => {
        const limitedValue = maxSelections
          ? (newValue as any[])?.slice(0, maxSelections) || []
          : newValue;
        handleChange(limitedValue);
      }}
      options={memberOptions}
      placeholder={placeholder}
      noOptionsMessage={() => "No members found"}
      loadingMessage={() => "Loading members..."}
      formatCreateLabel={(inputValue: string) =>
        creatable ? `Invite "${inputValue}" via email` : inputValue
      }
      isValidNewOption={(inputValue) =>
        creatable ? isValidEmail(inputValue) : false
      }
      styles={{
        control: (base) => ({
          ...base,
          minHeight: "40px",
          border: "1px solid #cbd5e0",
          borderRadius: "0px",
          "&:hover": {
            borderColor: "#a0aec0",
          },
          "&:focus-within": {
            borderColor: "#4299e1",
            boxShadow: "0 0 0 3px rgba(66, 153, 225, 0.1)",
          },
        }),
        multiValue: (base) => ({
          ...base,
          backgroundColor: "#f6f5f1",
          borderRadius: "0px",
        }),
        multiValueLabel: (base) => ({
          ...base,
          color: "#2d3748",
        }),
        multiValueRemove: (base, state) => ({
          ...base,
          display: lockedIds.includes(state.data.value) ? "none" : "flex",
          ":hover": {
            backgroundColor: "#000000",
            color: "white",
          },
        }),
      }}
    />
  );
};
